import { getAppConfig } from '@srichaitra/config';

/**
 * Where the access and refresh tokens live.
 *
 * Kept in Web Storage under keys named in config/app.ini, and mirrored in
 * memory so a read never touches the DOM API on the hot path. Which storage —
 * localStorage (survives a browser restart) or sessionStorage (cleared with the
 * tab) — is a deployment decision, not a code one, so it comes from the ini
 * file.
 *
 * Worth being honest about the trade-off: tokens in Web Storage are readable by
 * any script running on this origin, so this is only as safe as the app's
 * defence against XSS. The alternative — an httpOnly cookie — is not available
 * to us, because the backend authenticates with a bearer token and sets no
 * cookies. The mitigations that do apply are a strict CSP and keeping the
 * access token short-lived, which the server already does at 15 minutes.
 *
 * Every read and write is wrapped: Safari in private mode, and any browser with
 * site data blocked, throw on access rather than returning null. A storage
 * failure degrades to an in-memory session instead of a blank screen.
 */

let cache = { accessToken: null, refreshToken: null };
const listeners = new Set();

function storage() {
  const { storage: kind } = getAppConfig().session;
  return kind === 'session' ? globalThis.sessionStorage : globalThis.localStorage;
}

function keys() {
  const { accessTokenKey, refreshTokenKey } = getAppConfig().session;
  return { accessTokenKey, refreshTokenKey };
}

function readItem(key) {
  try {
    return storage().getItem(key);
  } catch {
    return null;
  }
}

function writeItem(key, value) {
  try {
    if (value === null) storage().removeItem(key);
    else storage().setItem(key, value);
  } catch {
    // In-memory only for this tab. Nothing else to do, and refusing to sign in
    // because storage is unavailable would be worse than a session that ends
    // when the tab closes.
  }
}

function notify() {
  for (const listener of listeners) listener(cache.accessToken);
}

/**
 * Loads whatever a previous session left behind.
 *
 * Called once at start-up, after the configuration is available and before the
 * first render, so a returning user is already signed in on the first paint
 * rather than seeing the login screen flash past.
 */
export function hydrateTokens() {
  const { accessTokenKey, refreshTokenKey } = keys();
  cache = {
    accessToken: readItem(accessTokenKey),
    refreshToken: readItem(refreshTokenKey)
  };
  return { ...cache };
}

export function getAccessToken() {
  return cache.accessToken;
}

export function getRefreshToken() {
  return cache.refreshToken;
}

export function setTokens({ accessToken, refreshToken }) {
  const { accessTokenKey, refreshTokenKey } = keys();

  cache = { accessToken: accessToken ?? null, refreshToken: refreshToken ?? null };
  writeItem(accessTokenKey, cache.accessToken);
  writeItem(refreshTokenKey, cache.refreshToken);
  notify();
}

export function clearTokens() {
  const { accessTokenKey, refreshTokenKey } = keys();

  cache = { accessToken: null, refreshToken: null };
  writeItem(accessTokenKey, null);
  writeItem(refreshTokenKey, null);
  notify();
}

/** Subscribes to token changes. Returns the unsubscribe function. */
export function onTokenChange(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
